import { observer } from "mobx-react";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import { Input } from "../../../../../components/inputs/input/input";
import { Toggle } from "../../../../../components/inputs/toggle/toggle";
import Card from "../../../../../components/layouts/card/card";
import {IValidationGroup} from "../../../../../hooks/validation-group/validation-group-hook";
import currentPortalStore from "../../../../../stores/current-portal-store";
import { FUNC } from "../../../../../stores/platform-service-store";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import DashTranslation from "../../../../../dashboard-translation/dashboard-translaton";
import TimezoneSettings from "./timezone-settings";
import Hr from "../../../../../components/hr/hr";
import MediaUpload from "../../../../../components/media-upload/media-upload";

interface IProps {
    onExpandClick: (expanded: boolean) => void;
    isExpanded: boolean;
    validationGroup: IValidationGroup;
    dataName?: string; /** used by Candu to target an element */
}

const StoreInformationCard: React.FC<IProps> = observer(({ onExpandClick, isExpanded, validationGroup, dataName }) => {
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-settings-store';

        const storeSetting = currentPortalStore.setting;
        const platform = currentPortalStore.platform;



    return (<Card
        toolbar={{
            title: (
                <FontBody1 variant={FONT_VARIANT.BOLD}>
                    <DashTranslation translationKey="STORE_INFORMATION" scope={pageScope} />
                </FontBody1>
            )
        }}
        expandCallback={onExpandClick}
        isExpanded={isExpanded}
        dataName={dataName}
    >
        {/* TODO: Uncomment this when adding PORTAL_DISPLAY_NAME translation */}
        {/* <FormFieldLine
            label={getTranslation('PORTAL_DISPLAY_NAME', pageScope)}
            note={getTranslation('PORTAL_DISPLAY_NAME_NOTE', pageScope)}
            dataName="portal-display-name"
        >
            <Input
                value={currentPortalStore.currentPortal.displayName ?? ''}
                onChange={currentPortalStore.currentPortal.setDisplayName}
                placeholder={currentPortalStore.currentPortal.name ?? ''}
            />
        </FormFieldLine> */}
        <FormFieldLine
            label={getTranslation('STORE_NAME', pageScope)}
            required
            note={getTranslation('STORE_NAME_IS_HOW_YOUR_CUSTOMER_SEE_YOUR_STORE_NAME', pageScope)}
            dataName="store-name"
        >
            <Input
                required
                validationGroup={validationGroup}
                value={storeSetting?.storeName}
                onChange={storeSetting?.setStoreName}
            />
        </FormFieldLine>
        <FormFieldLine
            label={getTranslation('STORE_URL', pageScope)}
            required
            note={getTranslation('THE_URL_OF_YOUR_STORE', pageScope)}
            dataName="store-url"
        >
            <Input
                required
                validationGroup={validationGroup}
                value={storeSetting?.storeUrl}
                onChange={storeSetting?.setStoreUrl}
            />
        </FormFieldLine>
        {platform?.isFunctionalitySupported(FUNC.FUNC_SET_ADMIN_PATH) && (
            <FormFieldLine
                label={getTranslation('STORE_ADMIN_PATH', pageScope)}
                note={getTranslation('THE_PATH_OF_YOUR_STORE_ADMIN_PAGE', pageScope)}
                dataName="store-admin-path"
            >
                <Input
                    value={storeSetting?.adminPath}
                    onChange={storeSetting?.setAdminPath}
                />
            </FormFieldLine>
        )}
        <FormFieldLine
            label={getTranslation('RETURN_POLICY_URL', pageScope)}
            tooltip={
                <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                    <DashTranslation translationKey="RETURN_POLICY_URL_TOOLTIP" scope={pageScope} />
                </FontBody2>
            }
            dataName="return-policy-url"
        >
            <Input
                value={storeSetting?.returnPolicyUrl}
                onChange={storeSetting?.setReturnPolicyUrl}
            />
        </FormFieldLine>
        <FormFieldLine
            label={getTranslation('CUSTOMER_SUPPORT', pageScope)}
            tooltip={
                <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                   <DashTranslation translationKey="CUSTOMER_SUPPORT_TOOLTIP" scope={pageScope} />
                </FontBody2>
            }
            dataName="customer-support"
        >
            <Input
                value={storeSetting?.customerSupport}
                onChange={storeSetting?.setCustomerSupport}
            />
        </FormFieldLine>
        <FormFieldLine
            label={getTranslation('STORE_CURRENCY', pageScope)}
            note={getTranslation('YOUR_DEFAULT_STORE_CURRENCY', pageScope)}
            dataName="store-currency"
        >
            <Input disabled value={currentPortalStore.currencyCode} />
        </FormFieldLine>
        <FormFieldLine
            label={getTranslation('MULTIPLE_CURRENCY_SUPPORT', pageScope)}
            note={getTranslation('ENABLE_TO_SUPPORT_MULTIPLE_CURRENCIES', pageScope)}
            dataName="multiple-currency-support"
        >
            <Toggle
                label=''
                checked={storeSetting?.supportMultipleCurrencies}
                onChange={storeSetting?.setSupportMultipleCurrencies}
            />
        </FormFieldLine>
        {storeSetting?.supportMultipleCurrencies && (<FormFieldLine
            label={getTranslation('ROUND_CONVERTED_PRICES', pageScope)}
            note={getTranslation('MATCH_WITH_STORE_SETTINGS_TO_DISPLAY_CORRECT_PRICES', pageScope)}
            dataName="round-converted-prices"
                    >
                        <Toggle
                label=''
                            checked={storeSetting?.roundConvertedPieces}
                            onChange={storeSetting?.setRoundConvertedPieces}
                        />
        </FormFieldLine>)}
        {storeSetting?.timezoneStore && (
            <TimezoneSettings
                timezoneStore={storeSetting?.timezoneStore}
            />
        )}
        <Hr />

        <FormFieldLine
            label={getTranslation("STORE_FAVICON", pageScope)}
            note={getTranslation("STORE_FAVICON_NOTE", pageScope)}
            dataName="store-favicon"
        >
            <MediaUpload
                fileTypes={[
                    "image/x-icon",
                    "image/vnd.microsoft.icon",
                    "image/png",
                    "image/svg+xml",
                ]}
                acceptedFileFormats=".ico,.png,.svg"
                mediaFiles={
                    storeSetting?.storeFavicon
                        ? [
                                {
                                    src: storeSetting?.storeFavicon,
                                    alt: "Favicon",
                                    mediaNotAvailable: false,
                                },
                            ]
                        : []
                }
                uploadMedia={(fileData, fileContent) => {
                    const content = fileContent as string;
                    const base64Content = content.replace(
                        `data:${fileData.type};base64,`,
                        ""
                    );

                    storeSetting?.setStoreFavicon(
                        fileData.type,
                        base64Content
                    );
                }}
                onDelete={() => {
                    storeSetting?.deleteStoreFavicon();
                }}
                buttonText="Upload image"
                isEditMode
            />
        </FormFieldLine>
        <FormFieldLine
            label={getTranslation("PAGE_TITLE", pageScope)}
            note={getTranslation("PAGE_TITLE_NOTE", pageScope)}
            dataName="page-title"
        >
            <Input
                value={storeSetting?.storePageTitle}
                onChange={storeSetting?.setStorePageTitle}
                placeholder={getTranslation(
                    "PAGE_TITLE_PLACEHOLDER",
                    pageScope
                )}
            />
        </FormFieldLine>
    </Card>
    );
});

export default StoreInformationCard;
